import { useState } from "react";
import { AlertCircle, Download, ExternalLink, ImageIcon, Loader2, Maximize2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import ImageLightbox from "@/components/ImageLightbox";

/**
 * The attachment panel shared by the buyer and admin support ticket modals.
 *
 * Both sides previously inlined the same static `<img>` with no way to see it
 * at full size — kept as one component so the two consoles cannot drift.
 */
/** Only https URLs (new uploads) and legacy inline images may be rendered or
 *  opened — anything else (e.g. a javascript: URL) is treated as no image. */
function isSafeImageUrl(url: string | null | undefined): url is string {
  return !!url && (url.startsWith("https://") || url.startsWith("data:image/"));
}

export default function TicketAttachment({
  url: rawUrl,
  label = "Attached image",
  emptyTitle = "No image attached",
  emptyHint,
}: {
  url: string | null | undefined;
  label?: string;
  emptyTitle?: string;
  emptyHint?: string;
}) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [downloading, setDownloading] = useState(false);
  const url = isSafeImageUrl(rawUrl) ? rawUrl : null;

  if (!url) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center rounded-xl border border-dashed border-border/60 bg-muted/5">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/50 text-muted-foreground/50 mb-4">
          <ImageIcon className="h-6 w-6" />
        </div>
        <p className="text-sm font-semibold text-foreground">{emptyTitle}</p>
        {emptyHint && <p className="text-xs text-muted-foreground mt-1 max-w-[220px]">{emptyHint}</p>}
      </div>
    );
  }

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(url, { mode: "cors" });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = url.split("/").pop()?.split("?")[0] || "attachment.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="rounded-xl border border-border/60 overflow-hidden shadow-sm">
        <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-muted/30 border-b border-border/50">
          <div className="flex items-center gap-2 min-w-0">
            <ImageIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground truncate">{label}</p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1.5 px-2.5 text-[11px]"
              onClick={handleDownload}
              disabled={downloading || status === "error"}
              title="Download the original file"
            >
              {downloading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
              Download
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1.5 px-2.5 text-[11px]"
              onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
              title="Open the image in a new browser tab"
            >
              <ExternalLink className="h-3 w-3" />
              Open
            </Button>
            <Button
              size="sm"
              className="h-7 gap-1.5 px-2.5 text-[11px]"
              onClick={() => setOpen(true)}
              disabled={status === "error"}
              title="View the image full size, with zoom"
            >
              <Maximize2 className="h-3 w-3" />
              View full size
            </Button>
          </div>
        </div>

        {status === "error" ? (
          <div className="bg-muted/20 p-6 flex flex-col items-center justify-center gap-2 min-h-[240px] text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-muted/50 text-muted-foreground/60">
              <AlertCircle className="h-5 w-5" />
            </div>
            <p className="text-sm font-semibold text-foreground">This image could not be loaded</p>
            <p className="text-xs text-muted-foreground max-w-[260px]">
              The file may have been removed from storage, or the link has expired.
            </p>
            <button
              onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
              className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground underline underline-offset-4 hover:text-foreground transition-colors"
            >
              Try opening it directly
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            title="Click to view full size"
            className="group relative w-full bg-muted/20 p-6 flex items-center justify-center min-h-[240px] cursor-zoom-in"
          >
            {status === "loading" && <Loader2 className="absolute h-5 w-5 animate-spin text-muted-foreground" />}
            <img
              src={url}
              alt={label}
              onLoad={() => setStatus("ready")}
              onError={() => setStatus("error")}
              className={`max-w-full max-h-[60dvh] rounded-xl object-contain shadow-md ring-1 ring-border/50 transition-opacity ${
                status === "loading" ? "opacity-0" : "opacity-100"
              }`}
            />
            {status === "ready" && (
              <span className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-semibold text-white opacity-0 group-hover:opacity-100 transition-opacity">
                <Maximize2 className="h-3 w-3" />
                Click to enlarge
              </span>
            )}
          </button>
        )}
      </div>

      {open && <ImageLightbox src={url} alt={label} caption={label} onClose={() => setOpen(false)} />}
    </>
  );
}
